import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BrandButton as Button, SurveySearchFilters } from "@/components";
import { useAuthenticatedApi } from "@/utils/callApi";
import PublishSurveyDialog from "@/components/PublishSurveyDialog";
import ConfirmModal from "@/components/ConfirmModal";
import Toast from "@/components/Toast";
import useToast from "@/components/useToast";
import DuplicateSurveyCard from "@/components/DuplicateSurveyCard";
import ReopenSurveyCard from "@/components/ReopenSurveyCard";
import DeleteSurveyCard from '@/components/DeleteSurveyCard';
import { repairInputListQuestionHeritage } from './heritageUtils';

/**
 * Survey Management - Container component for managing existing surveys
 * Handles duplicate and reopen operations
 */
export default function SurveyManager() {
  const { secureHelpers } = useAuthenticatedApi();
  const { toast, showToast, hideToast } = useToast();
  
  // Published surveys list
  const [publishedSurveys, setPublishedSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  // Survey search filters
  const [availableTags, setAvailableTags] = useState([]);
  const [activeTagFilter, setActiveTagFilter] = useState([]);
  const [currentDateRange, setCurrentDateRange] = useState({ modifiedAfter: null, modifiedBefore: null });
  
  // DUPLICATE SURVEY section
  const [selectedDuplicateSurveyId, setSelectedDuplicateSurveyId] = useState("");
  const [showTitleDialog, setShowTitleDialog] = useState(false);
  const [newSurveyTitle, setNewSurveyTitle] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  
  // REOPEN SURVEY section
  const [selectedReopenSurveyId, setSelectedReopenSurveyId] = useState("");
  
  // New survey after duplication
  const [newSurveyId, setNewSurveyId] = useState(null);
  const [operationType, setOperationType] = useState(""); // "duplicate" or "reopen"
  
  // Publish dialog state (shared by both functions)
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishSurveyTitle, setPublishSurveyTitle] = useState("");
  const [publishSurveyId, setPublishSurveyId] = useState(null);
  const [hospices, setHospices] = useState([]);
  const [selectedHospices, setSelectedHospices] = useState([]);
  const [publishToAll, setPublishToAll] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoClose, setAutoClose] = useState(false);

  // DELETE SURVEY section
  const [selectedDeleteSurveyId, setSelectedDeleteSurveyId] = useState("");
  const [hospiceInstances, setHospiceInstances] = useState([]);
  const [deleteInstanceCount, setDeleteInstanceCount] = useState(null);

  // get the selected survey id from the DeleteSurveyCard component and call the delete API endpoint with this id after confirmation from the user. Then remove the deleted survey from the list of published surveys and show a success toast. If error show error toast with message.
 
  //delete selected survey and all its data permanently from the database after confirmation 
const HandleDeleteClick = async (surveyId) => {  
    if (!surveyId) {
      alert("Please select a survey to delete");
      return;
    } 
    const survey = publishedSurveys.find(s => s.id === selectedDeleteSurveyId);
    // if survey then make delete api call with confirmation toast saying it has been deleted. Take user back to the main survey management page and remove the deleted survey from the list of published surveys. If error show error toast with message.
    if (survey) {
        try {
          await secureHelpers.admin.deleteSurvey(surveyId);
          showToast({
            message: `Survey "${survey.title}" has been permanently deleted.`,
            bgColor: "#28a745"
          });
          setPublishedSurveys(publishedSurveys.filter(s => s.id !== surveyId));
        } catch (error) {
          showToast({
            message: `Error deleting survey "${survey.title}": ${error.message}`,
            bgColor: "#dc3545"
          });
        }
    }
  };

  // call all instances of the 
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: "",
    message: "",
    variant: "warning",
    onConfirm: null
  });
  
  // Load published surveys and available tags on mount
  useEffect(() => {
    loadPublishedSurveys();
    loadAvailableTags();
  }, []);

  // Load instance count when a survey is selected for deletion
  useEffect(() => {
    if (!selectedDeleteSurveyId) {
      setDeleteInstanceCount(null);
      return;
    }
    const loadInstanceCount = async () => {
      try {
        // getSurveyInstanceGroups returns groups with instanceCount — find the one matching our survey ID
        const groups = await secureHelpers.admin.getSurveyInstanceGroups();
        const match = groups.find(g => {
          try { return JSON.parse(atob(g.key)).SurveyId === selectedDeleteSurveyId; }
          catch { return false; }
        });
        setDeleteInstanceCount(match ? match.instanceCount : 0);
      } catch (err) {
        console.error('Failed to load instance count for delete:', err);
        setDeleteInstanceCount(null);
      }
    };
    loadInstanceCount();
  }, [selectedDeleteSurveyId]);
  
  const loadPublishedSurveys = async () => {
    setLoading(true);
    try {
      const surveys = await secureHelpers.get('/api/surveys?isFinal=true');
      setPublishedSurveys(surveys);
    } catch (error) {
      console.error("Load error:", error);
      showToast({ 
        message: "Error loading published surveys: " + error.message,
        bgColor: "#dc3545"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTags = async () => {
    try {
      const tags = await secureHelpers.get('/api/surveys/tags');
      setAvailableTags(tags || []);
    } catch {
      // Tags are optional — silently ignore
    }
  };

  const fetchSurveysWithFilters = async ({ modifiedAfter, modifiedBefore }, tags) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ isFinal: 'true' });
      if (modifiedBefore) params.set('modifiedBefore', modifiedBefore);
      if (modifiedAfter) params.set('modifiedAfter', modifiedAfter);
      (tags || []).forEach(t => params.append('tags', t));
      const surveys = await secureHelpers.get(`/api/surveys?${params.toString()}`);
      setPublishedSurveys(surveys);
    } catch (error) {
      console.error("Survey search error:", error);
      showToast({ message: "Error searching surveys: " + error.message, bgColor: "#dc3545" });
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch surveys when user changes date filter
  const handleSurveyDateSearch = (dateRange) => {
    setCurrentDateRange(dateRange);
    fetchSurveysWithFilters(dateRange, activeTagFilter);
  };

  const handleTagFilterChange = (newTags) => {
    setActiveTagFilter(newTags);
    fetchSurveysWithFilters(currentDateRange, newTags);
  };
  
  const loadHospices = async () => {
    try {
      const hospicesList = await secureHelpers.get('/api/hospices');
      const sortedHospices = hospicesList.sort((a, b) => a.name.localeCompare(b.name));
      setHospices(sortedHospices);
    } catch (error) {
      console.error("Error loading hospices:", error);
      showToast({ 
        message: "Error loading hospices: " + error.message,
        bgColor: "#dc3545"
      });
    }
  };
  
  // DUPLICATE SURVEY FUNCTIONS
  const handleDuplicateClick = () => {
    if (!selectedDuplicateSurveyId) {
      alert("Please select a survey to duplicate");
      return;
    }
    const survey = publishedSurveys.find(s => s.id === selectedDuplicateSurveyId);
    if (survey) {
      setNewSurveyTitle(`${survey.title} (Copy)`);
      setShowTitleDialog(true);
    }
  };
 
  const duplicateSurvey = async () => {
    if (!newSurveyTitle.trim()) {
      alert("Please enter a title for the new survey");
      return;
    }
    
    const sourceSurvey = publishedSurveys.find(s => s.id === selectedDuplicateSurveyId);
    if (!sourceSurvey) return;
     
    setDuplicating(true);
    
    try {
      
      console.debug(`Duplicating survey ${sourceSurvey.id}: "${sourceSurvey.title}" as "${newSurveyTitle}"`);
      
      // 1. Load the original survey's steps and questions
      const stepsData = await secureHelpers.get(`/api/steps?surveyId=${sourceSurvey.id}`);
      stepsData.sort((a, b) => a.order - b.order);
      
      const questionsData = await secureHelpers.get(`/api/questions?surveyId=${sourceSurvey.id}`);
      questionsData.sort((a, b) => a.order - b.order);
      
      console.debug(`Loaded ${stepsData.length} steps and ${questionsData.length} questions`);
      
      // 2. Create a new survey (draft)
     const newSurvey = await secureHelpers.post('/api/surveys', {
        title: newSurveyTitle.trim(),
        heritageId: sourceSurvey.id,
        description: sourceSurvey.description || null,
        tags: sourceSurvey.tags || [],
        roles: sourceSurvey.roles || [],
        isFinal: false
      });
      
      console.log(`Created new survey with ID: ${newSurvey.id}`);
      const newId = newSurvey.id;
      setNewSurveyId(newId);
      
      // 3. Copy all steps
      const stepIdMap = {};
      for (const oldStep of stepsData) {
        const newStep = await secureHelpers.post('/api/steps', {
          surveyId: newId,
          heritageId: oldStep.id,
          name: oldStep.title || oldStep.name,
          description: oldStep.description,
          order: oldStep.order
        });
        stepIdMap[oldStep.id] = newStep.id;
        console.debug(`Copied step: ${oldStep.title || oldStep.name} -> ${newStep.id}`);
      }
      
      // 4. Copy all questions with heritage tracking
      const questionIdMap = {}; // Track old -> new question IDs for constraint updates
      const createdQuestions = []; // Store created questions with remapped data
      const remapReferenceSource = (source) => {
        const oldSourceQuestionId = parseInt(source.questionId || source.QuestionId);
        const newSourceQuestionId = questionIdMap[oldSourceQuestionId];

        if (!newSourceQuestionId) {
          console.warn(`⚠️ ReferenceSources: Could not find new ID for source question ${oldSourceQuestionId}`);
          console.warn(`⚠️ Available keys in questionIdMap:`, Object.keys(questionIdMap));
          return source;
        }

        return {
          QuestionId: newSourceQuestionId.toString(),
          ReferenceType: source.referenceType || source.ReferenceType || 'total'
        };
      };
      
      for (const oldQuestion of questionsData) {
        const newStepId = stepIdMap[oldQuestion.stepId];
        if (!newStepId) {
          console.warn(`Skipping question ${oldQuestion.id} - step not found`);
          continue;
        }
        
        // Parse typeParameters and add heritage tracking to choices/items/questions
        let newTypeParameters = oldQuestion.typeParameters;
        if (oldQuestion.typeParameters) {
          const oldParams = typeof oldQuestion.typeParameters === 'string' 
            ? JSON.parse(oldQuestion.typeParameters) 
            : oldQuestion.typeParameters;
          const oldLayout = typeof oldQuestion.layout === 'string'
            ? (() => { try { return JSON.parse(oldQuestion.layout || '{}'); } catch { return {}; } })()
            : (oldQuestion.layout || {});
          const repairedInputListHeritage = (oldQuestion.type === "inputList" || oldQuestion.type === "InputList")
            ? repairInputListQuestionHeritage(oldParams.Questions || [], oldLayout.columnHeadings || [])
            : [];

          let newParams = { ...oldParams };
          
          // Handle MultipleChoice: Choices array
          if (oldParams.Choices) {
            newParams.Choices = oldParams.Choices.map(choice => ({
              ...choice,
              Id: 0,    // Backend Choice.Id is required int (non-nullable) - use 0, backend will assign real IDs
              HeritageId: null
            }));
          }
          
          // Handle Questions array (used by InputList and MultipleChoice)
          if (oldParams.Questions) {
            newParams.Questions = oldParams.Questions.map((q, index) => ({
              ...q,
              Id: null,
              HeritageId: repairedInputListHeritage[index]?.heritageId
                ?? (q.HeritageId > 0 ? q.HeritageId : (q.Id > 0 ? q.Id : null))
            }));
          }
          
          // Handle ReferenceSources array (used by ReferenceValue)
          // NOTE: Question IDs will be remapped after all questions are copied
          if (oldParams.ReferenceSources) {
            newParams.ReferenceSources = oldParams.ReferenceSources.map(source => ({
              ...source,
              // Keep old questionId for now - will be remapped after all questions are copied
              questionId: source.questionId || source.QuestionId,
              referenceType: source.referenceType || source.ReferenceType || 'total'
            }));
          }
          
          // Preserve ReferenceFormula if present
          if (oldParams.ReferenceFormula !== undefined) {
            newParams.ReferenceFormula = oldParams.ReferenceFormula;
          }

          newTypeParameters = JSON.stringify(newParams);
        }
        
        // Remap constraint question IDs (e.g., sum_equals;359 -> sum_equals;460)
        const remappedConstraints = (oldQuestion.constraints || []).map(constraint => {
          // sum_equals;{oldQuestionId} or sum_equals;{oldQuestionId};total or sum_equals;{oldQuestionId};item-{index}
          if (constraint.startsWith('sum_equals;')) {
            const parts = constraint.split(';');
            const oldQuestionId = parseInt(parts[1]);
            const newQuestionId = questionIdMap[oldQuestionId];
            if (newQuestionId) {
              parts[1] = newQuestionId.toString();
              return parts.join(';');
            }
          }
          // equals_question;{oldQuestionId} or equals_question;{oldQuestionId};total
          if (constraint.startsWith('equals_question;')) {
            const parts = constraint.split(';');
            const oldQuestionId = parseInt(parts[1]);
            const newQuestionId = questionIdMap[oldQuestionId];
            if (newQuestionId) {
              parts[1] = newQuestionId.toString();
              return parts.join(';');
            }
          }
          return constraint;
        });
        
        // Remap condition question IDs (e.g., if;359;equals;text;Yes)
        const remappedConditions = (oldQuestion.conditions || []).map(condition => {
          if (condition.startsWith('if;')) {
            const parts = condition.split(';');
            const oldQuestionId = parseInt(parts[1]);
            const newQuestionId = questionIdMap[oldQuestionId];
            if (newQuestionId) {
              parts[1] = newQuestionId.toString();
              return parts.join(';');
            }
          }
          return condition;
        });
        
        const questionPayload = {
          surveyId: newId,
          heritageId: oldQuestion.id,
          stepId: newStepId,
          type: oldQuestion.type,
          text: oldQuestion.text,
          description: oldQuestion.description || null,
          isRequired: oldQuestion.isRequired ?? false,
          order: oldQuestion.order,
          layout: oldQuestion.layout || null,
          constraints: remappedConstraints,
          conditions: remappedConditions
        };
        
        if (newTypeParameters) {
          questionPayload.typeParameters = newTypeParameters;
        }
        
        const newQuestion = await secureHelpers.post('/api/questions', questionPayload);
        
        // Track old -> new question ID mapping
        questionIdMap[oldQuestion.id] = newQuestion.id;
        
        // Store created question with remapped data for later use
        createdQuestions.push({
          oldId: oldQuestion.id,
          newId: newQuestion.id,
          question: newQuestion,
          heritageId: oldQuestion.id,
          remappedConstraints: remappedConstraints,
          remappedConditions: remappedConditions
        });
        
        console.debug(`Copied question: ${oldQuestion.text.substring(0, 50)}...`);
      }
      
      // 5. Remap ReferenceSources questionIds in ReferenceValue questions
      console.debug("Remapping ReferenceSources for ReferenceValue questions...");
      console.debug("🔍 questionIdMap contents:", questionIdMap);
      console.debug("🔍 createdQuestions count:", createdQuestions.length);
      
      for (const createdQuestion of createdQuestions) {
        const oldQuestion = questionsData.find(q => q.id === createdQuestion.oldId);
        if (!oldQuestion || !oldQuestion.typeParameters) continue;
        
        const oldParams = typeof oldQuestion.typeParameters === 'string' 
          ? JSON.parse(oldQuestion.typeParameters) 
          : oldQuestion.typeParameters;
        const oldLayout = typeof oldQuestion.layout === 'string'
          ? (() => { try { return JSON.parse(oldQuestion.layout || '{}'); } catch { return {}; } })()
          : (oldQuestion.layout || {});
        
        // Check if this question has ReferenceSources/ReferenceColumns (ReferenceValue question)
        const hasReferenceSources = oldParams.ReferenceSources && oldParams.ReferenceSources.length > 0;
        const oldReferenceColumns = oldParams.ReferenceColumns || oldParams.referenceColumns || oldLayout.referenceColumns || [];
        const hasReferenceColumns = oldReferenceColumns.some(column => (column.ReferenceSources || column.referenceSources || []).length > 0);
        if (hasReferenceSources || hasReferenceColumns) {
          console.debug(`🔍 Processing ReferenceValue question ${createdQuestion.newId} (was ${createdQuestion.oldId})`);
          console.debug("🔍 Old ReferenceSources:", oldParams.ReferenceSources);
          
          const remappedSources = hasReferenceSources
            ? oldParams.ReferenceSources.map(remapReferenceSource)
            : [];
          const remappedReferenceColumns = oldReferenceColumns.map(column => ({
            ...column,
            ReferenceSources: (column.ReferenceSources || column.referenceSources || []).map(remapReferenceSource)
          }));
          
          console.debug("🔍 Remapped sources:", remappedSources);
          
          // Update the question with remapped sources
          const updatedParams = {
            ReferenceFormula: oldParams.ReferenceFormula || "",
            ReferenceSources: remappedSources,
            ReferenceDisplayMode: oldParams.ReferenceDisplayMode || oldParams.referenceDisplayMode || oldLayout.referenceDisplayMode,
            ReferenceRowLabel: oldParams.ReferenceRowLabel || oldParams.referenceRowLabel || oldLayout.referenceRowLabel,
            ReferenceColumns: remappedReferenceColumns
          };
          const existingLayout = typeof createdQuestion.question.layout === 'string'
            ? (() => { try { return JSON.parse(createdQuestion.question.layout || '{}'); } catch { return {}; } })()
            : (createdQuestion.question.layout || {});
          const updatedLayout = {
            ...existingLayout,
            ...(remappedReferenceColumns.length > 0 ? { referenceColumns: remappedReferenceColumns } : {})
          };
          
          console.debug("🔍 Final updatedParams being sent:", updatedParams);
          
          // Use the stored remapped constraints and conditions
          try {
            await secureHelpers.put(`/api/questions/${createdQuestion.newId}`, {
              id: createdQuestion.newId,
              surveyId: newId,
              heritageId: createdQuestion.heritageId,
              stepId: createdQuestion.question.stepId,
              text: createdQuestion.question.text,
              description: createdQuestion.question.description,
              isRequired: createdQuestion.question.isRequired ?? false,
              order: createdQuestion.question.order,
              type: createdQuestion.question.type,
              typeParameters: JSON.stringify(updatedParams),
              layout: JSON.stringify(updatedLayout),
              constraints: createdQuestion.remappedConstraints,
              conditions: createdQuestion.remappedConditions
            });
            
            console.log(`✅ Remapped ReferenceSources for question ${createdQuestion.newId} (was ${createdQuestion.oldId})`);
          } catch (updateError) {
            console.error(`⚠️ Failed to update ReferenceValue question ${createdQuestion.newId}:`, updateError);
            console.warn(`⚠️ Question was created but references may need manual fixing`);
            // Continue with copy anyway - question exists, just references might be wrong
          }
        }
      }
      
      console.log("Survey duplication complete!");
      
      // 6. Close title dialog and prepare for publishing
      setShowTitleDialog(false);
      setDuplicating(false);
      setOperationType("duplicate");
      setPublishSurveyTitle(newSurveyTitle);
      setPublishSurveyId(newId);
      
      await loadHospices();
      setShowPublishDialog(true);
      
    } catch (error) {
      console.error("Duplication error:", error);
      setDuplicating(false);
      alert(`Error duplicating survey: ${error.message}`);
    }
  };
  
  // REOPEN SURVEY FUNCTIONS
  const handleReopenClick = async () => {
    if (!selectedReopenSurveyId) {
      alert("Please select a survey to reopen");
      return;
    }
    
    const survey = publishedSurveys.find(s => s.id === selectedReopenSurveyId);
    if (!survey) return;
    
    setOperationType("reopen");
    setPublishSurveyTitle(survey.title);
    setPublishSurveyId(survey.id);
    
    await loadHospices();
    setShowPublishDialog(true);
  };
  
  // PUBLISH FUNCTION (shared by both)
  const publishSurvey = async () => {
    if (!publishSurveyId) {
      alert("No survey to publish.");
      return;
    }
    
    if (!endDate) {
      alert("Please select an end date");
      return;
    }
    
    if (!publishToAll && selectedHospices.length === 0) {
      alert("Please select at least one hospice");
      return;
    }
      const sourceSurvey = publishedSurveys.find(s => s.id === (operationType === "duplicate" ? selectedDuplicateSurveyId : selectedReopenSurveyId));

      const updatePayload = {
        id: publishSurveyId,
        title: publishSurveyTitle,
        description: sourceSurvey?.description || null,
        tags: sourceSurvey?.tags || [],
        roles: sourceSurvey?.roles || [],
        heritageId: sourceSurvey?.id || publishSurveyId,
        isFinal: true
      };

    // Show confirmation modal
    const hospiceCount = publishToAll ? hospices.length : selectedHospices.length;
    const confirmMessage = publishToAll 
      ? `You are about to publish this survey to ALL ${hospiceCount} hospices.\n\nAre you sure you want to continue?`
      : `You are about to publish this survey to ${hospiceCount} selected hospice(s).\n\nAre you sure you want to continue?`;
    
    setConfirmModalData({
      title: publishToAll ? "⚠️ Publish to All Hospices" : "Confirm Publish",
      message: confirmMessage,
      variant: publishToAll ? "danger" : "warning",
      onConfirm: async () => {
        setShowConfirmModal(false);
    try {
      // If duplicating, mark the new survey as final first
      if (operationType === "duplicate") {
        await secureHelpers.put(`/api/surveys/${publishSurveyId}`, updatePayload);
      }
      console.log("Update survey to final:", updatePayload);
      
      const publishData = {
        audience: publishToAll ? 0 : 1,  // Maps to PublishingAudience enum (All=0, List=1)
        audienceHospiceCodes: publishToAll ? null : selectedHospices,
        startDate: startDate,
        endDate: endDate,
        autoClose: autoClose
      };
      
      console.log(`Publishing survey (${operationType}) with data:`, publishData);
      
      // Publish the survey
      const result = await secureHelpers.post(`/api/surveys/${publishSurveyId}/publish`, publishData);
      console.log("Publish result:", result);

      setShowPublishDialog(false);
      
      // Reset form
      setPublishToAll(true);
      setSelectedHospices([]);
      setStartDate("");
      setEndDate("");
      setAutoClose(false);
      setNewSurveyId(null);
      setSelectedDuplicateSurveyId("");
      setSelectedReopenSurveyId("");
      
      showToast({ 
        message: `${result}\n\nThe survey is now available in the Member Data Portal!`,
        bgColor: "#28a745"
      });
      
      // Reload surveys list if we created a new one
      if (operationType === "duplicate") {
        loadPublishedSurveys();
      }
      
    } catch (error) {
      console.error("Publish error:", error);
      alert(`Error publishing survey: ${error.message}`);
    }
      }
    });
    setShowConfirmModal(true);
  };

  return (
  //  container to prevent scrolling issues with modals and toasts and to keep the page content nicely formatted. Set height to 100% to allow full height modals if needed. Overflow can be auto or hidden depending on whether you want scrollbars on the main page when modals are open.
  <div className="container-fluid px-3 px-lg-4">

      {toast.show && <Toast show={toast.show} message={toast.message} bgColor={toast.bgColor} onClose={hideToast} />}
      
      {/* Confirmation Modal */}
      <ConfirmModal
        show={showConfirmModal}
        title={confirmModalData.title}
        message={confirmModalData.message}
        variant={confirmModalData.variant}
        onConfirm={confirmModalData.onConfirm}
        onCancel={() => setShowConfirmModal(false)}
        confirmText="Yes, Publish"
        cancelText="Cancel"
      />
      
      <h2 style={{ color: "#F16D09" }}>Survey Management</h2>
      <p className="mb-4">
       Create new surveys by duplicating existing ones or reopen past surveys for additional data collection.
      </p>
      
      <SurveySearchFilters
        availableTags={availableTags}
        onDateRangeChange={handleSurveyDateSearch}
        onTagsChange={handleTagFilterChange}
        isSearching={loading}
      />

      {loading && (
        <div className="alert alert-info">
          <span className="spinner-border spinner-border-sm me-2"></span>
          Loading surveys…
        </div>
      )}

      {(() => {
        const displayedSurveys = publishedSurveys;
        const count = displayedSurveys.length;
        return (
          <>
            {!loading && (
              <p className="text-muted small mb-2">
                {count} survey{count !== 1 ? 's' : ''} found
              </p>
            )}
            <div className="row">
              <DuplicateSurveyCard
                publishedSurveys={displayedSurveys}
                selectedSurveyId={selectedDuplicateSurveyId}
                onSurveyChange={setSelectedDuplicateSurveyId}
                onDuplicateClick={handleDuplicateClick}
                loading={loading}
              />
              
              <ReopenSurveyCard
                publishedSurveys={displayedSurveys}
                selectedSurveyId={selectedReopenSurveyId}
                onSurveyChange={setSelectedReopenSurveyId}
                onReopenClick={handleReopenClick}
                loading={loading}
              />

              <DeleteSurveyCard
                publishedSurveys={displayedSurveys}
                selectedSurveyId={selectedDeleteSurveyId}
                onSurveyChange={setSelectedDeleteSurveyId}
                onDeleteClick={HandleDeleteClick}
                instanceCount={deleteInstanceCount}
                loading={loading}
              />
            </div>
          </>
        );
      })()}
 
      {/* New Survey Title Dialog (for duplication) */}
      {showTitleDialog && ReactDOM.createPortal(
        <>
          <div className="modal-backdrop show" style={{ zIndex: 10000 }}></div>
          <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 10001 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Name Your New Survey</h5>
                </div>
                <div className="modal-body">
                  <p>Copying: <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{publishedSurveys.find(s => s.id === selectedDuplicateSurveyId)?.title}</span></p>
                  <label className="form-label ">New Survey Title:</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newSurveyTitle}
                    onChange={(e) => setNewSurveyTitle(e.target.value)}
                    placeholder="e.g., February 2026 Survey"
                    autoFocus
                  />
                </div>
                <div className="modal-footer">
                  <Button 
                    onClick={duplicateSurvey}
                    disabled={duplicating || !newSurveyTitle.trim()}
                    variant="success"
                  >
                    {duplicating ? "Duplicating..." : "Duplicate & Publish"}
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowTitleDialog(false);
                      setNewSurveyTitle("");
                    }}
                    disabled={duplicating}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
      
      <PublishSurveyDialog
        show={showPublishDialog}
        onClose={() => {
          setShowPublishDialog(false);
          setNewSurveyId(null);
        }}
        surveyTitle={publishSurveyTitle}
        surveyId={publishSurveyId}
        onPublish={publishSurvey}
        hospices={hospices}
        selectedHospices={selectedHospices}
        setSelectedHospices={setSelectedHospices}
        publishToAll={publishToAll}
        setPublishToAll={setPublishToAll}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        autoClose={autoClose}
        setAutoClose={setAutoClose}
      />
    </div>
    );
}
